import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useClientPortalAuth } from '../../context/ClientPortalAuthContext';
import { clientPortalApi } from '../../services/api';
import { ClientPortalNotificationSummary } from '../../types';
import './ClientPortal.css';

const NOTIFICATION_POLL_INTERVAL_MS = 10000;

const EMPTY_NOTIFICATION_SUMMARY: ClientPortalNotificationSummary = {
  chat_unread_count: 0,
  ticket_unread_count: 0,
};

const ClientPortalLayout: React.FC = () => {
  const { user, logout } = useClientPortalAuth();
  const location = useLocation();
  const [notificationSummary, setNotificationSummary] = useState<ClientPortalNotificationSummary>(
    EMPTY_NOTIFICATION_SUMMARY
  );

  const canAccessSupportModules = !user?.must_change_password;

  const loadNotificationSummary = useCallback(async () => {
    if (!canAccessSupportModules) {
      setNotificationSummary(EMPTY_NOTIFICATION_SUMMARY);
      return;
    }

    try {
      const response = await clientPortalApi.getNotificationSummary();
      setNotificationSummary({
        chat_unread_count: Number(response.data?.chat_unread_count || 0),
        ticket_unread_count: Number(response.data?.ticket_unread_count || 0),
      });
    } catch {
      setNotificationSummary(EMPTY_NOTIFICATION_SUMMARY);
    }
  }, [canAccessSupportModules]);

  useEffect(() => {
    void loadNotificationSummary();
  }, [loadNotificationSummary, location.pathname]);

  useEffect(() => {
    if (!canAccessSupportModules) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      void loadNotificationSummary();
    }, NOTIFICATION_POLL_INTERVAL_MS);

    const handleSummaryRefresh = () => {
      void loadNotificationSummary();
    };

    window.addEventListener('client-portal-notifications-changed', handleSummaryRefresh);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('client-portal-notifications-changed', handleSummaryRefresh);
    };
  }, [canAccessSupportModules, loadNotificationSummary]);

  return (
    <div className="client-portal-shell">
      <header className="client-portal-header">
        <div className="client-portal-header-bar">
          <div className="client-portal-brand">
            <strong>Espace Client Winalco</strong>
            <span>
              {user?.client_name || 'Client'}
              {user?.email ? ` · ${user.email}` : ''}
            </span>
          </div>

          <nav className="client-portal-nav">
            <NavLink
              to="/client/profile"
              className={({ isActive }) => `client-portal-link${isActive ? ' active' : ''}`}
            >
              <span>Profil</span>
            </NavLink>
            <NavLink
              to="/client/tickets"
              className={({ isActive }) => `client-portal-link${isActive ? ' active' : ''}`}
            >
              <span>Tickets</span>
              {canAccessSupportModules && notificationSummary.ticket_unread_count > 0 && (
                <span className="client-portal-link-badge">
                  {notificationSummary.ticket_unread_count}
                </span>
              )}
            </NavLink>
            <NavLink
              to="/client/chat"
              className={({ isActive }) => `client-portal-link${isActive ? ' active' : ''}`}
            >
              <span>Chat</span>
              {canAccessSupportModules && notificationSummary.chat_unread_count > 0 && (
                <span className="client-portal-link-badge">
                  {notificationSummary.chat_unread_count}
                </span>
              )}
            </NavLink>
            <button type="button" className="btn btn-secondary" onClick={logout}>
              Se deconnecter
            </button>
          </nav>
        </div>
      </header>

      <main className="client-portal-main">
        <Outlet />
      </main>
    </div>
  );
};

export default ClientPortalLayout;
